import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
} from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TourItemStateDto, TourSummaryDto } from '../../models/tour.model';
import { TourService } from '../../services/tour.service';

@Component({
  selector: 'calsoft-tour-modal',
  templateUrl: './tour-modal.component.html',
  styleUrls: ['./tour-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourModalComponent {

  @Output() readonly exitTour = new EventEmitter<void>();

  readonly steps$: Observable<TourItemStateDto[]> = this.tourService.steps$;
  readonly currentStep$: Observable<TourItemStateDto | null> = this.tourService.currentStep$;
  readonly currentIndex$: Observable<number> = this.tourService.currentStepIndex$;
  readonly summary$: Observable<TourSummaryDto | null> = this.tourService.summary$;
  readonly loading$: Observable<boolean> = this.tourService.loading$;

  readonly hasNext$: Observable<boolean> = this.tourService.state$.pipe(
    map(s => s.currentStepIndex < (s.data?.items?.length ?? 1) - 1),
  );
  readonly hasPrev$: Observable<boolean> = this.tourService.state$.pipe(
    map(s => s.currentStepIndex > 0),
  );
  readonly isCurrentCompleted$: Observable<boolean> = this.currentStep$.pipe(
    map(step => step?.status === 'COMPLETED'),
  );
  readonly isAllDone$: Observable<boolean> = this.tourService.state$.pipe(
    map(s => {
      const items = s.data?.items ?? [];
      return items.length > 0 && items.every(i => i.status === 'COMPLETED' || i.status === 'SKIPPED');
    }),
  );

  constructor(readonly tourService: TourService) {}

  complete(): void {
    this.tourService.completeStep();
  }

  launchGuide(): void {
    this.tourService.playGuideForCurrentItem();
  }

  skip(): void {
    this.tourService.skipStep();
  }

  next(): void {
    this.tourService.nextStep();
  }

  prev(): void {
    this.tourService.prevStep();
  }

  exit(): void {
    this.exitTour.emit();
  }

  restartTour(): void {
    this.tourService.restartTour();
  }

  restartCurrentStep(): void {
    this.tourService.restartCurrentStep();
  }

  navigateToAction(step: TourItemStateDto): void {
    if (step.actionUrl) {
      window.open(step.actionUrl, '_blank', 'noopener noreferrer');
    } else if (step.routePath) {
      this.tourService.completeStep();
    }
  }

  getStatusLabel(step: TourItemStateDto): string {
    switch (step.status) {
      case 'COMPLETED': return 'Done';
      case 'IN_PROGRESS': return 'In Progress';
      case 'SKIPPED': return 'Skipped';
      default: return 'Not Started';
    }
  }

  getBadgeIcon(status: TourItemStateDto['status']): string {
    switch (status) {
      case 'COMPLETED': return 'check_circle';
      case 'IN_PROGRESS': return 'play_circle';
      case 'SKIPPED': return 'skip_next';
      default: return 'radio_button_unchecked';
    }
  }

  trackByStateId(_: number, item: TourItemStateDto): number {
    return item.stateId;
  }
}
